import React, { useCallback, useEffect, useState } from 'react';
import Avatar from '../../../component/avatar';
import { getProjectArchiveData, updateProjectData } from '../../../services/projectServices';
import AppLayout from '../../../component/appLayout';
import {
  AssignGroup1,
  Content,
  Group,
  HeaderBlock,
  HeaderTitle,
  Heading,
  Icon,
  ProjectItem,
  ProjectName,
  Text,
  Title,
  RightContent
} from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import ProjectAllLoading from '../../../component/loading/projectAllLoading';
import Modal from '../../../component/modals/modal';
import DeleteModal from '../../../component/modals/deleteModal';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { captureException } from '../../../services/logService';
import { AVATAR_BORDER, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE, MENU_OPTIONS } from '../../../global/constants';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { rootStore } from '../../../mobx/rootStore';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import ResponsiveHeader from '../../../component/responsiveHeader';
import { useMobileDevice } from '../../../global/useMobile';
import { observer } from 'mobx-react-lite';
import { ProjectModel } from '../../../mobx/models/project';
import IconButton from '../../../component/button/iconButton';

const ArchiveProject: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  const [openModel, setOpenModel] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [projectId, setProjectId] = useState('');

  // Mobx store variables
  const { projectStore, workspaceStore } = rootStore;
  const { archiveProjects } = projectStore;
  const { currentWorkspace } = workspaceStore;

  // Other variables
  const mobile = useMobileDevice();
  const menuItems = [
    {
      key: MENU_OPTIONS.RESTORE,
      label: 'Restore'
    },
    {
      key: MENU_OPTIONS.DELETE,
      label: 'Delete'
    }
  ];

  const loadData = useCallback(async () => {
    try {
      if (currentWorkspace?.id) {
        setLoading(true);
        await getProjectArchiveData();
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onClickRestore = useCallback((id: string) => {
    setOpenModel('Restore');
    setProjectId(id);
  }, []);

  const closeRestoreModel = useCallback(() => {
    setProjectId('');
    setOpenModel(null);
  }, []);

  const onOkRestore = useCallback(async () => {
    try {
      setSubmitLoading(true);
      if (!currentWorkspace?.isAdmin)
        return rootStore.alertNotificationStore.setErrorMessage(`You can't Restore project.`);
      const updatePayload = {
        isArchived: false
      };
      const result = await updateProjectData(projectId, updatePayload);
      if (result) {
        rootStore.projectStore.removeArchiveProjects([projectId]);
        trackAnalyticActivity(PROJECT_ANALYTICS.RESTORED);
        closeRestoreModel();
        rootStore.alertNotificationStore.setSuccessMessage('Project Restore Successfully!');
      }
    } catch (e) {
      captureException(e);
    } finally {
      setSubmitLoading(false);
    }
  }, [closeRestoreModel, projectId, currentWorkspace?.isAdmin]);

  const onClickDelete = useCallback((id: string) => {
    setOpenModel('Delete');
    setProjectId(id);
  }, []);

  const closeDeleteModel = useCallback(() => {
    setProjectId('');
    setOpenModel(null);
  }, []);

  const onDeleteProject = useCallback(async () => {
    try {
      setSubmitLoading(true);
      if (!currentWorkspace?.isAdmin)
        return rootStore.alertNotificationStore.setErrorMessage(`You can't delete project.`);
      const payload = {
        IsDeleted: true
      };
      const result = await updateProjectData(projectId, payload);
      if (result) {
        rootStore.projectStore.removeArchiveProjects([projectId]);
        trackAnalyticActivity(PROJECT_ANALYTICS.DELETED);
        closeDeleteModel();
        rootStore.alertNotificationStore.setSuccessMessage('Project Deleted Successfully!');
      }
    } catch (e) {
      captureException(e);
    } finally {
      setSubmitLoading(false);
    }
  }, [closeDeleteModel, projectId, currentWorkspace?.isAdmin]);

  const handleMenuClick = useCallback(
    (key: number | string, item: ProjectModel) => {
      key = Number(key);
      if (key === MENU_OPTIONS.RESTORE) {
        onClickRestore(item?.id);
      } else if (key === MENU_OPTIONS.DELETE) {
        onClickDelete(item?.id);
      }
    },
    [onClickRestore, onClickDelete]
  );

  return (
    <>
      <AppLayout>
        <Group className='desktop-content'>
          {!mobile ? (
            <HeaderBlock>
              <Heading>
                <Icon>
                  <SVGIcon
                    name='project-icon'
                    width={ICON_SIZE.XXL}
                    height={ICON_SIZE.XXL}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--brand-primary)'
                  />
                </Icon>
                <HeaderTitle>Archived Projects</HeaderTitle>
              </Heading>
            </HeaderBlock>
          ) : (
            <ResponsiveHeader title='Archived Projects' />
          )}
          {loading && <ProjectAllLoading />}
          {!loading && archiveProjects?.length === 0 && <>No Archive Projects</>}
          {!loading && (
            <Content>
              {archiveProjects?.length > 0 &&
                archiveProjects?.map((item, index) => {
                  return (
                    <ProjectItem key={item?.id}>
                      <ProjectName>
                        <Title>{item?.name}</Title>
                        <Text>by {item?.createdBy?.name}</Text>
                      </ProjectName>
                      <RightContent>
                        <AssignGroup1>
                          <ul>
                            {item?.users?.map((img, index) => {
                              if (index > 3) return <React.Fragment key={img?.id}></React.Fragment>;
                              return (
                                <li key={img?.id}>
                                  <Avatar
                                    imgSrc={img?.profile_photo || ''}
                                    name={img?.name ? img.name : 'U N'}
                                    size={24}
                                    borderVariant={AVATAR_BORDER.MEDIUM}
                                  />
                                </li>
                              );
                            })}
                            {item?.users?.length > 4 && <li className='plus-icon'>+{item?.users?.length - 4}</li>}
                          </ul>
                        </AssignGroup1>
                        <Dropdown
                          activeClassName='active'
                          content={
                            <IconButton
                              iconName='three-dots-horizontal-icon'
                              size={ICON_BUTTON_SIZE.SMALL}
                              variant={ICON_BUTTON_TYPE.TERTIARY}
                            />
                          }
                          trigger='click'>
                          {menuItems.map((menuItem) => (
                            <DropdownItem
                              key={menuItem.key}
                              label={menuItem.label}
                              tone={menuItem.label === 'Delete' ? 'critical' : 'primary'}
                              onClick={() => handleMenuClick(menuItem.key, item)}
                            />
                          ))}
                        </Dropdown>
                      </RightContent>
                    </ProjectItem>
                  );
                })}
            </Content>
          )}
        </Group>
      </AppLayout>
      <Modal open={openModel === 'Restore'} onClose={closeRestoreModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeRestoreModel}
          onOk={onOkRestore}
          isYes={true}
          loading={submitLoading}
          modalTitle='Restore Project?'
          description='Are you sure you want to restore this project?'
          isPrimaryButton={true}
        />
      </Modal>
      <Modal open={openModel === 'Delete'} onClose={closeDeleteModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeDeleteModel}
          onOk={onDeleteProject}
          loading={submitLoading}
          modalTitle='Delete Project?'
          description='Are you sure you want to delete this project?'
        />
      </Modal>
      <BottomSubNavigation />
    </>
  );
};

export default observer(ArchiveProject);
